import { logError } from '../../helpers/logger.helper';
import type { GraphLink } from '../indexedDb.helper';

/**
 * clean issues:
 * - remove any links where the target and source are the same
 */
export const cleanLinks = (links: GraphLink[]): GraphLink[] => {
  return links?.filter((link) => link.source !== link.target);
};

/**
 * Look through links and find node IDs that are descendants of the start node
 */
export const findDescendantNodeIds = (
  startNodeId: number,
  links: GraphLink[] = []
): number[] => {
  // Build adjacency map once: O(L). Avoids O(L) filter() per visited node.
  const childrenBySource = new Map<number, number[]>();
  for (const link of links) {
    const list = childrenBySource.get(link.source);
    if (list) {
      list.push(link.target);
    } else {
      childrenBySource.set(link.source, [link.target]);
    }
  }

  const visited = new Set<number>([startNodeId]);
  const descendants: number[] = [];
  // Use a stack with index pointer instead of Array.shift() (O(N) per shift).
  const stack: number[] = [startNodeId];

  while (stack.length > 0) {
    // Safe non-null assertion: guarded by `stack.length > 0` above.
    const currentId = stack.pop()!;
    const children = childrenBySource.get(currentId);
    if (!children) {
      continue;
    }
    for (const childId of children) {
      if (!visited.has(childId)) {
        visited.add(childId);
        stack.push(childId);
        descendants.push(childId);
      }
    }
  }

  return descendants;
};

/**
 * Look through links and find node IDs that are ancestors of the start node
 */
export const findParentNodeIDs = (nodeId, links: GraphLink[]): number[] => {
  const parents: number[] = [];
  let currentId = nodeId;

  while (currentId) {
    const link = links?.find((l) => l.target === currentId);
    if (!link) {
      break;
    }
    if (parents.includes(link.source)) {
      logError(
        new Error(
          `Circular reference detected or repeated source: ${link.source}`
        )
      );
      break;
    }
    parents.push(link.source);
    currentId = link.source;
  }

  return parents;
};
